/**
 * CountdownTimer — Pre-Launch banner shown on the homepage between the
 * sponsored banner stack and the intro video.
 *
 * Self-contained: fetches /api/public/launch-status on mount, ticks every
 * second, and re-fetches every 30 s so the operator flipping the toggle
 * propagates to anonymous visitors within ~30 s without a hard reload.
 *
 * Renders NOTHING when ``pre_launch_mode_enabled`` is false — so it's
 * safe to leave mounted on the homepage indefinitely.
 *
 * UI policy:
 *  - All times shown in America/New_York (Eastern). The operator types the
 *    launch date in the admin (any timezone-aware ISO string); we render
 *    it back in EST for consumer clarity.
 *  - Days / Hours / Minutes / Seconds tiles.
 *  - "We're Live!" message when the countdown reaches zero (operator
 *    still has to manually disable the toggle to unlock purchases — by
 *    design, per operator confirmation).
 */

import { Rocket } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { fetchPublicLaunchStatus } from '@/api/member';

const REFRESH_INTERVAL_MS = 60_000;

const _pad = (n) => String(Math.max(0, Math.floor(n))).padStart(2, '0');

/** Format an ISO timestamp in America/New_York for display ("Jul 4, 2026 — 8:00 PM EDT"). */
const _formatEastern = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(d);
  } catch (_e) {
    return '';
  }
};

const _diffParts = (targetMs) => {
  const now = Date.now();
  let diff = targetMs - now;
  const expired = diff <= 0;
  diff = Math.max(0, diff);
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  diff -= days * 24 * 60 * 60 * 1000;
  const hours = Math.floor(diff / (60 * 60 * 1000));
  diff -= hours * 60 * 60 * 1000;
  const minutes = Math.floor(diff / (60 * 1000));
  diff -= minutes * 60 * 1000;
  const seconds = Math.floor(diff / 1000);
  return { days, hours, minutes, seconds, expired };
};

export const CountdownTimer = () => {
  const [status, setStatus] = useState({
    pre_launch_mode_enabled: false,
    launch_at_iso: null,
  });
  const [, setTick] = useState(0);

  // Initial + periodic refetch — operator toggles propagate to anonymous
  // visitors without requiring a hard reload.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const data = await fetchPublicLaunchStatus();
        if (alive) {
          setStatus({
            pre_launch_mode_enabled: !!data?.pre_launch_mode_enabled,
            launch_at_iso: data?.launch_at_iso || null,
          });
        }
      } catch (_e) {
        // Silent fail: countdown is decorative; do not block the
        // homepage if the public endpoint is briefly unavailable.
      }
    };
    load();
    const refresh = setInterval(load, REFRESH_INTERVAL_MS);
    return () => {
      alive = false;
      clearInterval(refresh);
    };
  }, []);

  // 1-second visual tick (cheap — no network).
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const targetMs = useMemo(() => {
    if (!status.launch_at_iso) return null;
    const d = new Date(status.launch_at_iso);
    const ms = d.getTime();
    return Number.isNaN(ms) ? null : ms;
  }, [status.launch_at_iso]);

  // Bail out cleanly when not in pre-launch mode.
  if (!status.pre_launch_mode_enabled) return null;

  const easternStr = _formatEastern(status.launch_at_iso);
  const parts = targetMs ? _diffParts(targetMs) : null;

  return (
    <section className="px-4 md:px-8 pt-4" data-testid="pre-launch-countdown-section" aria-label="Pre-launch countdown">
      <div className="max-w-[960px] mx-auto">
        <div
          className="sc-glass rounded-2xl px-5 md:px-8 py-5 md:py-6 relative overflow-hidden"
          style={{
            borderColor: 'rgba(255, 182, 18, 0.42)',
            background: 'linear-gradient(135deg, rgba(255,182,18,0.06), rgba(255,182,18,0.02) 70%)',
            boxShadow: '0 0 0 1px rgba(255,182,18,0.18), 0 10px 32px rgba(0,0,0,0.55)',
          }}
        >
          {/* Decorative top-right gold halo */}
          <div
            className="absolute -top-12 -right-12 w-48 h-48 pointer-events-none rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(255,182,18,0.22), transparent 60%)',
            }}
            aria-hidden="true"
          />

          {/* Header row */}
          <div className="flex items-center gap-3 mb-3 relative">
            <div
              className="inline-flex items-center justify-center w-9 h-9 rounded-full"
              style={{
                background: 'rgba(255,182,18,0.14)',
                border: '1px solid rgba(255,182,18,0.5)',
              }}
              aria-hidden="true"
            >
              <Rocket className="w-4 h-4 text-[#FFC20E]" />
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold" style={{ color: '#FFC20E' }} data-testid="pre-launch-eyebrow">
                Pre-Launch Mode
              </div>
              <div className="font-display text-lg md:text-xl font-bold leading-tight" style={{ color: 'var(--sc-text)' }} data-testid="pre-launch-title">
                {parts?.expired ? 'We\u2019re Live!' : 'Launching Soon'}
              </div>
            </div>
          </div>

          {/* Body: either countdown grid or pending-launch message */}
          {parts ? (
            <>
              <div className="grid grid-cols-4 gap-2 md:gap-3 mt-2" data-testid="pre-launch-countdown-grid">
                {[
                  { label: 'Days', value: parts.days, key: 'd' },
                  { label: 'Hours', value: parts.hours, key: 'h' },
                  { label: 'Minutes', value: parts.minutes, key: 'm' },
                  { label: 'Seconds', value: parts.seconds, key: 's' },
                ].map(({ label, value, key }) => (
                  <div
                    key={key}
                    className="rounded-xl py-3 md:py-4 text-center"
                    style={{
                      background: 'rgba(0,0,0,0.34)',
                      border: '1px solid rgba(255,182,18,0.32)',
                    }}
                    data-testid={`pre-launch-tile-${key}`}
                  >
                    <div
                      className="font-display font-bold leading-none"
                      style={{
                        fontSize: 'clamp(1.6rem, 4.5vw, 2.4rem)',
                        color: '#FFC20E',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                      data-testid={`pre-launch-value-${key}`}
                    >
                      {_pad(value)}
                    </div>
                    <div className="mt-1.5 text-[10px] md:text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--sc-text-mute)' }}>
                      {label}
                    </div>
                  </div>
                ))}
              </div>
              {easternStr ? (
                <div
                  className="mt-3 md:mt-4 text-center text-[11.5px] md:text-[12.5px]"
                  style={{ color: 'var(--sc-text-mute)' }}
                  data-testid="pre-launch-launch-at-display"
                >
                  Launch: <span style={{ color: '#FFC20E', fontWeight: 600 }}>{easternStr}</span>
                </div>
              ) : null}
            </>
          ) : (
            <div className="text-[12.5px] md:text-[13px] leading-relaxed" style={{ color: 'var(--sc-text-mute)' }} data-testid="pre-launch-no-date-note">
              Purchases are temporarily paused. Deposits remain open so you can fund your wallet ahead of launch.
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CountdownTimer;
